'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface PlanActionsProps {
  planId: string;
  planName: string;
  onSavePlan: (planId: string, planName: string) => void;
  onExportPDF: (planId: string) => void;
  onSharePlan: (planId: string) => void;
  onModifyInputs: () => void;
  isAuthenticated?: boolean;
}

const PlanActions = ({ 
  planId, 
  planName, 
  onSavePlan, 
  onExportPDF, 
  onSharePlan, 
  onModifyInputs, 
  isAuthenticated
}: PlanActionsProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const router = useRouter();

  const handleSavePlan = async () => {
    const router = useRouter();

    if (!isAuthenticated) {
      // Redirect to login for authentication before saving
      router.push('/login');
      return;
    }

    setIsSaving(true);
    try {
      await onSavePlan(planId, planName);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await onExportPDF(planId);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSharePlan = () => {
    onSharePlan(planId);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-6">Plan Actions</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Save Plan */}
        <button
          onClick={handleSavePlan}
          disabled={isSaving || !isAuthenticated}
          className="flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isSaving ? (
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
          ) : (
            <Icon name="BookmarkIcon" size={20} />
          )}
          <span className="font-medium">
            {!isAuthenticated ? 'Sign in to Save' : isSaving ? 'Saving...' : 'Save to My Plans'}
          </span>
        </button>

        {/* Export PDF */}
        <button
          onClick={handleExportPDF}
          disabled={isExporting}
          className="flex items-center justify-center space-x-2 px-4 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isExporting ? (
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
          ) : (
            <Icon name="DocumentArrowDownIcon" size={20} />
          )}
          <span className="font-medium">
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </span>
        </button>

        {/* Share Plan */}
        <button
          onClick={handleSharePlan}
          className="flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors duration-200"
        >
          <Icon name="ShareIcon" size={20} />
          <span className="font-medium">Share Plan</span>
        </button>

        {/* Modify Inputs */}
        <button
          onClick={onModifyInputs}
          className="flex items-center justify-center space-x-2 px-4 py-3 bg-surface text-foreground border border-border rounded-lg hover:bg-muted transition-colors duration-200"
        >
          <Icon name="PencilSquareIcon" size={20} />
          <span className="font-medium">Modify Inputs</span>
        </button>
      </div>

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="mt-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center space-x-2">
          <Icon name="CheckCircleIcon" size={20} className="text-success" />
          <span className="text-sm font-medium text-success">Plan saved successfully!</span>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-md font-semibold text-foreground mb-4">Quick Actions</h4>
        <div className="grid grid-cols-2 gap-3">
          <button className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
            <Icon name="ArrowUturnLeftIcon" size={16} />
            <span>Start Over</span>
          </button>
          <button className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
            <Icon name="DocumentDuplicateIcon" size={16} />
            <span>Duplicate Plan</span>
          </button>
          <button className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
            <Icon name="ScaleIcon" size={16} />
            <span>Compare Plans</span>
          </button>
          <button className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors duration-200">
            <Icon name="QuestionMarkCircleIcon" size={16} />
            <span>Get Help</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanActions;